import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
  Divider,
  Chip,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  PlayArrow as PlayIcon,
  Bolt as BoltIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { Client } from '@langchain/langgraph-sdk';
import { getTranscriptionSessionById } from '@/services/transcriptionSessionService';
import {
  updateCurrentThreadId,
  getCachedThreadId,
  cacheThreadId,
  ThreadType
} from '@/services/threadService';
import { QuestionCard } from './QuestionCard';
import type {
  TranscriptionSession,
  SpeakerSegmentResult
} from '@/types/transcriptionSessionTypes';

const LANGGRAPH_BASE_URL =
  import.meta.env.VITE_LANGGRAPH_BASE_URL || 'http://localhost:2024';

interface SelfIntakeMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  isStreaming?: boolean;
}

export interface SelfIntakeInterfaceProps {
  sessionId: string;
  onSessionUpdated?: () => Promise<void> | void;
}

const parseMessageContent = (content: string) => {
  const parts = [];
  let currentText = '';
  let index = 0;

  // Define the special tags we want to handle
  const specialTags = [
    { start: '<question>', end: '</question>', type: 'question' }
  ];

  while (index < content.length) {
    // Find the next special tag (whichever comes first)
    let nextTag = null;
    let nextTagStart = content.length;

    for (const tag of specialTags) {
      const tagStart = content.indexOf(tag.start, index);
      if (tagStart !== -1 && tagStart < nextTagStart) {
        nextTagStart = tagStart;
        nextTag = tag;
      }
    }

    // If no more special tags found, add remaining content as text
    if (!nextTag) {
      currentText += content.slice(index);
      break;
    }

    // Add any text before the special tag
    if (nextTagStart > index) {
      currentText += content.slice(index, nextTagStart);
    }

    // Find the end of the special tag
    const tagEnd = content.indexOf(nextTag.end, nextTagStart);
    if (tagEnd === -1) {
      // No closing tag found, treat as regular text
      currentText += content.slice(nextTagStart);
      break;
    }

    // Add accumulated text as a text part
    if (currentText) {
      parts.push({ type: 'text', content: currentText });
      currentText = '';
    }

    // Extract and parse the tag content
    const tagContent = content.slice(
      nextTagStart + nextTag.start.length,
      tagEnd
    );

    try {
      const tagData = JSON.parse(tagContent);
      parts.push({ type: nextTag.type, data: tagData });
    } catch (error) {
      console.error(`Failed to parse ${nextTag.type} data:`, error);
      // If parsing fails, treat as regular text
      currentText += content.slice(nextTagStart, tagEnd + nextTag.end.length);
    }

    index = tagEnd + nextTag.end.length;
  }

  // Add any remaining text
  if (currentText) {
    parts.push({ type: 'text', content: currentText });
  }

  return parts;
};

const createLangGraphClient = (apiUrl: string, token: string, userId: string) =>
  new Client({
    apiUrl,
    defaultHeaders: {
      Authorization: `Bearer ${token}`,
      'X-User-Id': userId
    }
  });

const getAuthToken = async (
  getAccessTokenSilently: any,
  getAccessTokenWithPopup: any
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    if (
      error?.error === 'consent_required' ||
      error?.error === 'login_required' ||
      error?.error === 'interaction_required'
    ) {
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) throw new Error('Interactive token acquisition failed.');
      return token;
    }
    throw error;
  }
};

function mapSegmentsToMessages(
  segments: SpeakerSegmentResult[]
): SelfIntakeMessage[] {
  const sorted = [...segments].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return sorted.map((s) => ({
    id: s.id,
    role: s.speakerIndex === 0 ? 'user' : 'assistant',
    content: s.textContent,
    timestamp: s.timestamp
  }));
}

const MessageBubble = React.memo(
  ({
    m,
    onAnswer
  }: {
    m: SelfIntakeMessage;
    onAnswer: (a: string) => void;
  }) => {
    const renderMessageContent = (message: SelfIntakeMessage) => {
      if (message.role === 'user') {
        return (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        );
      }

      const parts = parseMessageContent(message.content);

      return (
        <Box>
          {parts.map((part, index) => {
            if (part.type === 'text') {
              return (
                <Typography
                  key={index}
                  variant="body2"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {part.content}
                </Typography>
              );
            } else if (part.type === 'question') {
              return (
                <QuestionCard
                  key={index}
                  questionData={part.data}
                  compact={true}
                  onAnswerSelected={onAnswer}
                />
              );
            }
            return null;
          })}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start',
          mb: 2,
          animation: 'fadeInUp 0.3s ease-out',
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(10px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}
      >
        <Box
          sx={{
            maxWidth: '75%',
            display: 'flex',
            flexDirection: m.role === 'user' ? 'row-reverse' : 'row',
            alignItems: 'flex-start',
            gap: 1.5
          }}
        >
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: m.role === 'user' ? '#3b82f6' : '#f3f4f6',
              color: m.role === 'user' ? '#ffffff' : '#374151',
              flexShrink: 0
            }}
          >
            {m.role === 'user' ? (
              <PersonIcon sx={{ fontSize: 18 }} />
            ) : (
              <BotIcon sx={{ fontSize: 18 }} />
            )}
          </Avatar>
          <Box
            sx={{
              backgroundColor: m.role === 'user' ? '#3b82f6' : '#f8fafc',
              color: m.role === 'user' ? '#ffffff' : '#374151',
              borderRadius: '16px',
              px: 2.5,
              py: 1.5,
              boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
              border: m.role === 'user' ? 'none' : '1px solid #e2e8f0'
            }}
          >
            <Box
              sx={{
                '& .MuiTypography-root': {
                  color: m.role === 'user' ? '#ffffff' : '#374151'
                }
              }}
            >
              {renderMessageContent(m)}
            </Box>
            <Typography
              variant="caption"
              sx={{
                mt: 1,
                display: 'block',
                color:
                  m.role === 'user' ? 'rgba(255, 255, 255, 0.7)' : '#6b7280',
                fontSize: '11px'
              }}
            >
              {new Date(m.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
              {m.isStreaming && (
                <>
                  {' • '}
                  <CircularProgress
                    size={10}
                    sx={{
                      ml: 0.5,
                      color:
                        m.role === 'user'
                          ? 'rgba(255, 255, 255, 0.7)'
                          : '#6b7280'
                    }}
                  />
                  AI is thinking…
                </>
              )}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

const ChatMessages = React.memo(
  ({
    messages,
    endRef,
    onAnswer
  }: {
    messages: SelfIntakeMessage[];
    endRef: React.RefObject<HTMLDivElement>;
    onAnswer: (a: string) => void;
  }) => (
    <Box>
      {messages.map((m) => (
        <MessageBubble key={m.id} m={m} onAnswer={onAnswer} />
      ))}
      <div ref={endRef} />
    </Box>
  )
);

export const SelfIntakeInterface: React.FC<SelfIntakeInterfaceProps> = ({
  sessionId,
  onSessionUpdated
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();

  const [messages, setMessages] = useState<SelfIntakeMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [input, setInput] = useState('');
  const [threadIdState, setThreadIdState] = useState<string | null>(null);
  const [focusMode, setFocusMode] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const endRef = useRef<HTMLDivElement>(null!);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const isAtBottomRef = useRef<boolean>(true);
  const [session, setSession] = useState<TranscriptionSession | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const hasLoadedOnceRef = useRef<boolean>(false);
  const rafIdRef = useRef<number | null>(null);
  const pendingAssistantUpdateRef = useRef<{
    id: string;
    content: string;
  } | null>(null);
  const pendingAppendMapRef = useRef<Record<string, string>>({});

  const canSend = useMemo(
    () => !!sessionId && !!appUser?.id && input.trim().length > 0 && !isSending,
    [sessionId, appUser?.id, input, isSending]
  );

  const scrollToEnd = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToEnd();
    }
  }, [messages, scrollToEnd]);

  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const thresholdPx = 48; // consider near-bottom within this threshold
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isAtBottomRef.current = distanceFromBottom <= thresholdPx;
  }, []);

  const reloadFromSession = useCallback(
    async (refreshMessages = false) => {
      if (!sessionId || !appUser?.id) return;
      const shouldShowLoading = !hasLoadedOnceRef.current || refreshMessages;
      if (shouldShowLoading) setIsLoading(true);
      try {
        const s: TranscriptionSession | null =
          await getTranscriptionSessionById(
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        if (s) {
          // Only update session if it has actually changed to prevent unnecessary re-renders
          setSession((prev) => {
            if (
              !prev ||
              prev.id !== s.id ||
              JSON.stringify(prev.templateQuestions) !==
                JSON.stringify(s.templateQuestions) ||
              prev.speakerSegments?.length !== s.speakerSegments?.length
            ) {
              return s;
            }
            return prev;
          });
        }
        // Only reload messages on initial load or if explicitly requested
        if (
          s?.speakerSegments &&
          (!hasLoadedOnceRef.current || refreshMessages)
        ) {
          setMessages(mapSegmentsToMessages(s.speakerSegments));
        }
      } catch (e) {
        console.error('Failed to load self-intake conversation:', e);
      } finally {
        if (shouldShowLoading) setIsLoading(false);
      }
    },
    [sessionId, appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup]
  );

  useEffect(() => {
    // Only attempt initial load when we have prerequisites
    if (!hasLoadedOnceRef.current && sessionId && appUser?.id) {
      (async () => {
        await reloadFromSession();
        hasLoadedOnceRef.current = true;
      })();
    }
    // Try cached thread id (if user navigated back and forth)
    if (sessionId) {
      const cached = getCachedThreadId(sessionId, ThreadType.SelfIntake);
      if (cached) setThreadIdState(cached);
    }
  }, [reloadFromSession, sessionId, appUser?.id]);

  // Helper to throttle assistant content updates to once per animation frame
  const scheduleAssistantUpdate = useCallback(
    (messageId: string, content: string) => {
      pendingAssistantUpdateRef.current = { id: messageId, content };
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(() => {
          const pending = pendingAssistantUpdateRef.current;
          rafIdRef.current = null;
          if (pending) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === pending.id ? { ...m, content: pending.content } : m
              )
            );
            pendingAssistantUpdateRef.current = null;
          }
        });
      }
    },
    []
  );

  const scheduleAssistantAppend = useCallback(
    (messageId: string, delta: string) => {
      pendingAppendMapRef.current[messageId] =
        (pendingAppendMapRef.current[messageId] || '') + delta;
      if (rafIdRef.current == null) {
        rafIdRef.current = requestAnimationFrame(() => {
          const map = pendingAppendMapRef.current;
          pendingAppendMapRef.current = {};
          rafIdRef.current = null;
          const ids = Object.keys(map);
          if (ids.length > 0) {
            setMessages((prev) =>
              prev.map((m) => {
                const d = map[m.id];
                return d ? { ...m, content: m.content + d } : m;
              })
            );
          }
        });
      }
    },
    []
  );

  useEffect(() => {
    return () => {
      if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
    };
  }, []);

  const resolveOrCreateThreadId = useCallback(
    async (client: Client): Promise<string> => {
      if (!sessionId || !appUser?.id)
        throw new Error('Missing session or user');
      if (threadIdState) return threadIdState;
      const created = await client.threads.create();
      const tid = created.thread_id;
      setThreadIdState(tid);
      cacheThreadId(sessionId, tid, ThreadType.SelfIntake);
      // Persist to backend (non-blocking)
      updateCurrentThreadId(
        sessionId,
        appUser.id,
        tid,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        ThreadType.SelfIntake
      ).catch((err) => console.warn('Failed to persist thread id:', err));
      // Tiny delay before first stream
      await new Promise((r) => setTimeout(r, 250));
      return tid;
    },
    [
      threadIdState,
      sessionId,
      appUser?.id,
      getAccessTokenSilently,
      getAccessTokenWithPopup
    ]
  );

  const handleSend = useCallback(async () => {
    if (!canSend) return;
    const content = input.trim();
    setInput('');
    // Optimistic UI; do not refetch entire session afterwards
    setIsSending(true);
    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      abortControllerRef.current = new AbortController();
      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser!.id
      );
      const threadId = await resolveOrCreateThreadId(client);

      const userMessage: SelfIntakeMessage = {
        id: uuidv4(),
        role: 'user',
        content,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, userMessage]);

      const assistantMessage: SelfIntakeMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      setMessages((prev) => [...prev, assistantMessage]);

      const stream = client.runs.stream(threadId, 'self_intake_flow', {
        input: {
          messages: [{ type: 'human', content }],
          session_id: sessionId,
          user_id: appUser!.id,
          backend_auth_token: token
        },
        config: {
          configurable: {
            authorization: `Bearer ${token}`,
            session_id: sessionId,
            user_id: appUser!.id,
            thread_id: threadId
          }
        },
        metadata: {
          session_id: sessionId,
          thread_id: threadId,
          conversation_id: threadId,
          transcription_session_id: sessionId,
          user_id: appUser!.id
        },
        streamMode: ['messages', 'updates'],
        streamSubgraphs: true,
        signal: abortControllerRef.current.signal
      });

      for await (const sse of stream as any) {
        const event = sse?.event as string | undefined;
        const data = sse?.data;

        // If we receive a post-model hook update, the run is complete
        if (
          typeof event === 'string' &&
          event.startsWith('updates|') &&
          data &&
          typeof data === 'object' &&
          'post_model_hook' in data
        ) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          // Refresh session metadata only (not messages - we already have them from stream)
          await reloadFromSession(false);
          if (onSessionUpdated) await onSessionUpdated();
          continue;
        }

        if (event === 'end' || event === 'messages/complete') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          // Refresh session metadata only (not messages - we already have them from stream)
          await reloadFromSession(false);
          if (onSessionUpdated) await onSessionUpdated();
          // Do not refetch all segments here; the turn is already reflected
          continue;
        }
        if (event === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id
                ? {
                    ...m,
                    isStreaming: false,
                    content: data?.message || 'An error occurred.'
                  }
                : m
            )
          );
          continue;
        }
        if (event === 'messages/partial' || event === 'messages/complete') {
          if (Array.isArray(data)) {
            const ai = data.find((m: any) => m.type === 'ai');
            if (ai && ai.content) {
              scheduleAssistantUpdate(assistantMessage.id, ai.content);
              if (event === 'messages/complete') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessage.id
                      ? { ...m, isStreaming: false }
                      : m
                  )
                );
              }
            }
          }
          continue;
        }
        if (data?.content && typeof data.content === 'string') {
          // Stop thinking indicator on first content
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          scheduleAssistantAppend(assistantMessage.id, data.content);
        }
      }
    } catch (err: any) {
      if (err?.name !== 'AbortError') {
        console.error('Self intake send failed:', err);
        setMessages((prev) => [
          ...prev,
          {
            id: uuidv4(),
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
            timestamp: new Date().toISOString()
          }
        ]);
      }
    } finally {
      setIsSending(false);
      abortControllerRef.current = null;
    }
  }, [
    canSend,
    input,
    sessionId,
    appUser?.id,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    resolveOrCreateThreadId,
    reloadFromSession,
    onSessionUpdated
  ]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleBegin = useCallback(async () => {
    // Kick off the intake with a clear instruction for the agent
    setIsSending(true);
    try {
      const token = await getAuthToken(
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      const client = createLangGraphClient(
        LANGGRAPH_BASE_URL,
        token,
        appUser!.id
      );
      const threadId = await resolveOrCreateThreadId(client);

      // Reuse the same send path but with a standard opening
      const opening = 'Begin the self intake now.';
      const userMessage: SelfIntakeMessage = {
        id: uuidv4(),
        role: 'user',
        content: opening,
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, userMessage]);

      const assistantMessage: SelfIntakeMessage = {
        id: uuidv4(),
        role: 'assistant',
        content: '',
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      setMessages((prev) => [...prev, assistantMessage]);

      const stream = client.runs.stream(threadId, 'self_intake_flow', {
        input: {
          messages: [{ type: 'human', content: opening }],
          session_id: sessionId,
          user_id: appUser!.id,
          backend_auth_token: token
        },
        config: {
          configurable: {
            authorization: `Bearer ${token}`,
            session_id: sessionId,
            user_id: appUser!.id,
            thread_id: threadId
          }
        },
        metadata: {
          session_id: sessionId,
          thread_id: threadId,
          conversation_id: threadId,
          transcription_session_id: sessionId,
          user_id: appUser!.id
        },
        streamMode: ['messages', 'updates'],
        streamSubgraphs: true
      });

      for await (const sse of stream as any) {
        const event = sse?.event as string | undefined;
        const data = sse?.data;

        // If we receive a post-model hook update, the run is complete
        if (
          typeof event === 'string' &&
          event.startsWith('updates|') &&
          data &&
          typeof data === 'object' &&
          'post_model_hook' in data
        ) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          await reloadFromSession(false);
          if (onSessionUpdated) await onSessionUpdated();
          continue;
        }

        if (event === 'end' || event === 'messages/complete') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          await reloadFromSession(false);
          if (onSessionUpdated) await onSessionUpdated();
          continue;
        }
        if (event === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id
                ? {
                    ...m,
                    isStreaming: false,
                    content: data?.message || 'An error occurred.'
                  }
                : m
            )
          );
          continue;
        }
        if (event === 'messages/partial' || event === 'messages/complete') {
          if (Array.isArray(data)) {
            const ai = data.find((m: any) => m.type === 'ai');
            if (ai && ai.content) {
              scheduleAssistantUpdate(assistantMessage.id, ai.content);
              if (event === 'messages/complete') {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMessage.id
                      ? { ...m, isStreaming: false }
                      : m
                  )
                );
              }
            }
          }
          continue;
        }
        if (data?.content && typeof data.content === 'string') {
          // Stop thinking indicator on first content
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMessage.id ? { ...m, isStreaming: false } : m
            )
          );
          scheduleAssistantAppend(assistantMessage.id, data.content);
        }
      }
    } catch (e) {
      console.error('Failed to begin self intake:', e);
    } finally {
      setIsSending(false);
    }
  }, [
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    appUser?.id,
    resolveOrCreateThreadId,
    sessionId,
    reloadFromSession,
    onSessionUpdated
  ]);

  // Derived UI data
  const totalQuestions = session?.templateQuestions?.length || 0;
  const answeredQuestions =
    session?.templateQuestions?.filter((q) => q.isComplete).length || 0;
  const progressPct = totalQuestions
    ? Math.round((answeredQuestions / totalQuestions) * 100)
    : 0;
  const firstUnanswered = useMemo(() => {
    return (
      session?.templateQuestions?.find((q) => !q.isComplete)?.questionText || ''
    );
  }, [session?.templateQuestions]);

  const categories = useMemo(() => {
    const map = new Map<string, { total: number; answered: number }>();
    (session?.templateQuestions || []).forEach((q) => {
      const key = q.category || 'General';
      const prev = map.get(key) || { total: 0, answered: 0 };
      map.set(key, {
        total: prev.total + 1,
        answered: prev.answered + (q.isComplete ? 1 : 0)
      });
    });
    const list = Array.from(map.entries()).map(([name, stats]) => ({
      name,
      ...stats
    }));
    list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [session?.templateQuestions]);

  const filteredMessages = useMemo(() => {
    if (!isDesktop || selectedCategory === 'All') return messages;
    // If a category is selected, only show messages after that category was first asked.
    // As we cannot perfectly map, keep all messages for simplicity to avoid losing context.
    return messages;
  }, [isDesktop, selectedCategory, messages]);

  // Layout
  return (
    <Box
      sx={{
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {isDesktop ? (
        <Grid
          container
          spacing={focusMode ? 0 : 1.5}
          sx={{
            flex: 1,
            minHeight: 0,
            maxHeight: '100%',
            p: focusMode ? 0 : 1
          }}
        >
          {/* Left: Progress & Outline */}
          {!focusMode && (
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{ display: 'flex', minHeight: 0, maxHeight: '100%' }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '10px',
                  backgroundColor: '#ffffff',
                  boxShadow:
                    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden'
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Progress
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <LinearProgress
                      variant="determinate"
                      value={progressPct}
                      sx={{
                        flex: 1,
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: '#f3f4f6',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor:
                            progressPct === 100 ? '#10b981' : '#3b82f6',
                          borderRadius: 4
                        }
                      }}
                    />
                    <Chip
                      size="small"
                      label={`${progressPct}%`}
                      sx={{
                        backgroundColor:
                          progressPct === 100 ? '#dcfce7' : '#dbeafe',
                        color: progressPct === 100 ? '#059669' : '#2563eb',
                        fontWeight: 600
                      }}
                    />
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '13px' }}
                  >
                    {answeredQuestions} of {totalQuestions} questions completed
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Next Question
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#f8fafc',
                      border: '1px solid #e2e8f0',
                      p: 2.5,
                      borderRadius: '8px',
                      position: 'relative'
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        lineHeight: 1.5,
                        color: firstUnanswered ? '#374151' : '#6b7280'
                      }}
                    >
                      {firstUnanswered ||
                        'Waiting for AI to ask the first question…'}
                    </Typography>
                    {firstUnanswered && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -1,
                          left: -1,
                          width: 4,
                          height: '100%',
                          backgroundColor: '#3b82f6',
                          borderRadius: '4px 0 0 4px'
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 2, flexShrink: 0 }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Sections
                    </Typography>
                    <Chip
                      size="small"
                      label="All"
                      color={selectedCategory === 'All' ? 'primary' : 'default'}
                      onClick={() => setSelectedCategory('All')}
                      variant={
                        selectedCategory === 'All' ? 'filled' : 'outlined'
                      }
                      sx={{
                        borderRadius: '6px',
                        fontWeight: 500
                      }}
                    />
                  </Stack>
                  <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    <List dense disablePadding>
                      {categories.map((c) => (
                        <ListItemButton
                          key={c.name}
                          selected={selectedCategory === c.name}
                          onClick={() => setSelectedCategory(c.name)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography variant="body2">
                                  {c.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {c.answered}/{c.total}
                                </Typography>
                              </Stack>
                            }
                          />
                        </ListItemButton>
                      ))}
                      {categories.length === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No sections available.
                        </Typography>
                      )}
                    </List>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Center: Conversation */}
          <Grid
            size={{ xs: 12, md: focusMode ? 12 : 6 }}
            sx={{ display: 'flex', minHeight: 0, maxHeight: '100%' }}
          >
            <Box
              sx={{
                borderRadius: focusMode ? 0 : '10px',
                backgroundColor: '#ffffff',
                boxShadow: focusMode
                  ? 'none'
                  : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'hidden'
              }}
            >
              {/* Chat Header with Focus Toggle */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: focusMode ? 2 : 2,
                  pb: 1,
                  px: focusMode ? 3 : 2
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: '#374151' }}
                >
                  Self-Intake Interview
                </Typography>
                <Tooltip
                  title={
                    focusMode
                      ? 'Exit Focus Mode'
                      : 'Focus Mode - Hide side panels'
                  }
                  placement="left"
                >
                  <IconButton
                    onClick={() => setFocusMode(!focusMode)}
                    size="small"
                    sx={{
                      backgroundColor: focusMode ? '#dbeafe' : '#f3f4f6',
                      color: focusMode ? '#2563eb' : '#6b7280',
                      '&:hover': {
                        backgroundColor: focusMode ? '#bfdbfe' : '#e5e7eb'
                      },
                      borderRadius: '6px'
                    }}
                  >
                    {focusMode ? (
                      <FullscreenExitIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <FullscreenIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              <Box
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: focusMode ? 3 : 2,
                  minHeight: 0,
                  maxHeight: '100%'
                }}
                ref={messagesContainerRef}
                onScroll={handleMessagesScroll}
              >
                {isLoading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={28} sx={{ mb: 1 }} />
                    <Typography color="text.secondary">
                      Loading self-intake…
                    </Typography>
                  </Box>
                ) : filteredMessages.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: '#dbeafe',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3
                      }}
                    >
                      <BoltIcon sx={{ fontSize: 40, color: '#3b82f6' }} />
                    </Box>
                    <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                      Begin Self Intake
                    </Typography>
                    <Typography
                      color="text.secondary"
                      sx={{ mb: 4, fontSize: '15px', lineHeight: 1.5 }}
                    >
                      I'll guide you through the template one question at a
                      time. Just answer naturally and I'll help you complete
                      everything.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={handleBegin}
                      startIcon={
                        isSending ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <PlayIcon />
                        )
                      }
                      disabled={isSending || !appUser?.id}
                      sx={{
                        backgroundColor: '#3b82f6',
                        '&:hover': {
                          backgroundColor: '#2563eb'
                        },
                        borderRadius: '8px',
                        px: 4,
                        py: 1.5,
                        fontSize: '15px',
                        fontWeight: 600
                      }}
                    >
                      {isSending ? 'Starting…' : 'Start Interview'}
                    </Button>
                  </Box>
                ) : (
                  <ChatMessages
                    messages={filteredMessages}
                    endRef={endRef}
                    onAnswer={setInput}
                  />
                )}
              </Box>

              {/* Composer */}
              <Box
                sx={{ p: focusMode ? 3 : 2, borderTop: '1px solid #e5e7eb' }}
              >
                <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder={
                      firstUnanswered
                        ? `Answer: ${firstUnanswered}`
                        : 'Type your answer…'
                    }
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={!appUser?.id || isSending}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px'
                      }
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSend}
                    disabled={!canSend}
                    sx={{
                      backgroundColor: canSend ? '#2563eb' : 'transparent',
                      color: canSend ? '#ffffff' : '#9ca3af',
                      '&:hover': {
                        backgroundColor: canSend ? '#1d4ed8' : 'transparent'
                      },
                      borderRadius: '8px'
                    }}
                  >
                    {isSending ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SendIcon />
                    )}
                  </IconButton>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '11px',
                    fontFamily: 'monospace'
                  }}
                >
                  Press Enter to send • ESC to clear •{' '}
                  {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+F for
                  focus mode
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Right: Session details */}
          {!focusMode && (
            <Grid
              size={{ xs: 12, md: 3 }}
              sx={{ display: 'flex', minHeight: 0, maxHeight: '100%' }}
            >
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '10px',
                  backgroundColor: '#ffffff',
                  boxShadow:
                    '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2.5,
                  minHeight: 0,
                  maxHeight: '100%',
                  overflow: 'hidden'
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Session
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {session?.title || 'Untitled Session'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '13px' }}
                  >
                    {new Date(
                      session?.sessionStartTime || Date.now()
                    ).toLocaleString()}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    Tips
                  </Typography>
                  <Stack spacing={2}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          mt: 0.75,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '13px', lineHeight: 1.5 }}
                      >
                        Keep responses concise; I'll follow up for specifics.
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: '#3b82f6',
                          mt: 0.75,
                          flexShrink: 0
                        }}
                      />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: '13px', lineHeight: 1.5 }}
                      >
                        You can skip if unsure; we'll return later.
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      ) : (
        // Mobile layout
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            maxHeight: '100%',
            overflow: 'hidden',
            p: 1,
            gap: 1.5
          }}
        >
          {/* Summary header */}
          <Box
            sx={{
              p: 2,
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ flex: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={progressPct}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    mb: 0.5,
                    backgroundColor: '#f3f4f6',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor:
                        progressPct === 100 ? '#10b981' : '#3b82f6',
                      borderRadius: 4
                    }
                  }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '13px' }}
                >
                  {answeredQuestions} of {totalQuestions} completed
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${progressPct}%`}
                sx={{
                  backgroundColor: progressPct === 100 ? '#dcfce7' : '#dbeafe',
                  color: progressPct === 100 ? '#059669' : '#2563eb',
                  fontWeight: 600
                }}
              />
            </Stack>
            {firstUnanswered && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  position: 'relative'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 500, mb: 0.5, color: '#374151' }}
                >
                  Next Question:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: '#6b7280', lineHeight: 1.4 }}
                >
                  {firstUnanswered}
                </Typography>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -1,
                    left: -1,
                    width: 4,
                    height: '100%',
                    backgroundColor: '#3b82f6',
                    borderRadius: '4px 0 0 4px'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* Conversation */}
          <Box
            sx={{
              flex: 1,
              borderRadius: '10px',
              backgroundColor: '#ffffff',
              boxShadow:
                '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              maxHeight: '100%',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 2,
                minHeight: 0,
                maxHeight: '100%'
              }}
              ref={messagesContainerRef}
              onScroll={handleMessagesScroll}
            >
              {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress size={28} sx={{ mb: 1 }} />
                  <Typography color="text.secondary">Loading…</Typography>
                </Box>
              ) : messages.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
                  <Box
                    sx={{
                      width: 70,
                      height: 70,
                      borderRadius: '50%',
                      backgroundColor: '#dbeafe',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 2.5
                    }}
                  >
                    <BoltIcon sx={{ fontSize: 36, color: '#3b82f6' }} />
                  </Box>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Begin Self Intake
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ mb: 3, fontSize: '14px', lineHeight: 1.5 }}
                  >
                    I'll guide you through the template one question at a time.
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleBegin}
                    startIcon={
                      isSending ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : (
                        <PlayIcon />
                      )
                    }
                    disabled={isSending || !appUser?.id}
                    sx={{
                      backgroundColor: '#3b82f6',
                      '&:hover': {
                        backgroundColor: '#2563eb'
                      },
                      borderRadius: '8px',
                      px: 3,
                      py: 1.25,
                      fontSize: '14px',
                      fontWeight: 600
                    }}
                  >
                    {isSending ? 'Starting…' : 'Start Interview'}
                  </Button>
                </Box>
              ) : (
                <ChatMessages
                  messages={messages}
                  endRef={endRef}
                  onAnswer={setInput}
                />
              )}
            </Box>

            {/* Composer */}
            <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
              <Stack direction="row" spacing={1.5} sx={{ mb: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  maxRows={3}
                  placeholder={
                    firstUnanswered
                      ? `Answer: ${firstUnanswered}`
                      : 'Type your answer…'
                  }
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={!appUser?.id || isSending}
                  variant="outlined"
                  size="small"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px'
                    }
                  }}
                />
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={!canSend}
                  sx={{
                    backgroundColor: canSend ? '#2563eb' : 'transparent',
                    color: canSend ? '#ffffff' : '#9ca3af',
                    '&:hover': {
                      backgroundColor: canSend ? '#1d4ed8' : 'transparent'
                    },
                    borderRadius: '8px'
                  }}
                >
                  {isSending ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <SendIcon />
                  )}
                </IconButton>
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}
              >
                Press Enter to send • ESC to clear •{' '}
                {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+F for
                focus mode
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SelfIntakeInterface;
